import { DiagnosticResult, RiskTolerance } from '@/types/portfolio';
import { StatusBadge } from './StatusBadge';
import { EducationPopup } from './EducationPopup';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronRight, ArrowRight, Sparkles, AlertTriangle, CheckCircle2, XCircle, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { STATUS_LABELS, ScoringConfig, DEFAULT_SCORING_CONFIG } from '@/lib/scoring-config';
import { MiniDonutChart, PotentialSavings, SectorBreakdown, HorizontalCompareBar, StressTestChart, SharpeComparison } from './MiniCharts';

interface DiagnosticCardProps {
  name: string;
  subtitle?: string;
  iconName: string;
  categoryKey: string;
  result: DiagnosticResult;
  onViewDetails: () => void;
  scoringConfig?: ScoringConfig;
  riskTolerance?: RiskTolerance;
  animationDelay?: number; // For staggered entrance
}

// Helper to format percentages
const formatPct = (value: number, decimals = 1): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

// Helper to format currency
const formatCurrency = (value: number): string => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

// Data-driven explanations that use actual portfolio metrics
const getDataDrivenExplanation = (
  status: 'GREEN' | 'YELLOW' | 'RED',
  categoryKey: string,
  details: Record<string, unknown>
): string => {
  // If no data available, show a prompt to add data
  const hasData = Object.keys(details).length > 0;
  if (!hasData) {
    return "Add portfolio data to see personalized insights.";
  }

  switch (categoryKey) {
    case 'riskDiversification':
    case 'diversification': {
      const topPositions = details.topPositions as Array<{ ticker: string; weight: number }> | undefined;
      const top3Weight = (details.top3Weight as number) ?? 0;
      const topTicker = topPositions?.[0]?.ticker;
      const topWeight = topPositions?.[0]?.weight ?? 0;

      if (!topTicker) return "Add holdings to analyze concentration.";

      if (status === 'GREEN') {
        return `Well diversified: ${topTicker} is your largest at ${formatPct(topWeight)}, with top 3 totaling ${formatPct(top3Weight)}.`;
      } else if (status === 'YELLOW') {
        return `${topTicker} at ${formatPct(topWeight)} is above typical thresholds. Consider if this concentration aligns with your goals.`;
      }
      return `High concentration: ${topTicker} alone is ${formatPct(topWeight)} of your portfolio—a significant single-stock risk.`;
    }

    case 'downsideResilience': {
      const maxDrawdown = (details.maxDrawdownPct as number) ?? (details.avgImpact as number) ?? 0;
      const criticalCount = (details.criticalCount as number) ?? 0;
      const drawdownPct = Math.abs(maxDrawdown);

      if (status === 'GREEN') {
        return `In a market crash, you'd likely see around ${formatPct(drawdownPct, 0)} decline—manageable for most investors.`;
      } else if (status === 'YELLOW') {
        return `A ${formatPct(drawdownPct, 0)} potential drop is notable. Ensure your emergency fund covers 6+ months.`;
      }
      return `${formatPct(drawdownPct, 0)} potential decline with ${criticalCount} high-risk holdings. Review your risk tolerance.`;
    }

    case 'costAnalysis': {
      const allInFees = (details.allInFees as number) ?? 0;
      const tenYearImpact = (details.tenYearImpact as number) ?? 0;
      const highFeeCount = (details.highFeeHoldings as unknown[])?.length ?? 0;

      if (status === 'GREEN') {
        return `Fees of ${formatPct(allInFees)} are competitive. You're keeping more of your returns.`;
      } else if (status === 'YELLOW') {
        return `${formatPct(allInFees)} in fees costs ~${formatCurrency(tenYearImpact)} over 10 years. ${highFeeCount} holdings could be optimized.`;
      }
      return `High fees: ${formatPct(allInFees)} annually = ${formatCurrency(tenYearImpact)} lost over 10 years. Review ${highFeeCount} expensive holdings.`;
    }

    case 'taxEfficiency': {
      const harvestable = (details.totalHarvestable as number) ?? 0;
      const taxSavings = (details.estimatedTaxSavings as number) ?? 0;
      const inefficientCount = (details.inefficientInTaxable as unknown[])?.length ?? 0;

      if (harvestable > 0) {
        return `${formatCurrency(harvestable)} in losses available to harvest for ~${formatCurrency(taxSavings)} in tax savings.`;
      } else if (inefficientCount > 0) {
        return `${inefficientCount} tax-inefficient holdings in taxable accounts. Moving them could reduce your tax bill.`;
      } else if (status === 'GREEN') {
        return "Tax-efficient placement: bonds in retirement accounts, stocks in taxable. Well structured.";
      }
      return "Review asset location—some holdings may generate unnecessary taxes.";
    }

    case 'riskAdjusted': {
      const probability = (details.probability as number) ?? 50;
      const sharpeRatio = (details.sharpeRatio as number);

      if (sharpeRatio !== undefined) {
        if (status === 'GREEN') {
          return `Sharpe ratio of ${sharpeRatio.toFixed(2)} indicates good risk-adjusted returns for your volatility level.`;
        }
        return `Sharpe ratio of ${sharpeRatio.toFixed(2)} suggests room to improve returns relative to risk taken.`;
      }

      if (status === 'GREEN') {
        return `${probability.toFixed(0)}% success probability—your plan is on solid ground.`;
      }
      return `${probability.toFixed(0)}% success rate. Small adjustments could meaningfully improve your odds.`;
    }

    case 'planningGaps': {
      const completed = (details.completed as number) ?? 0;
      const total = (details.total as number) ?? 11;
      const criticalMissing = (details.criticalMissing as string[]) ?? [];

      if (completed === total) {
        return `All ${total} planning items complete. Your financial foundation is solid.`;
      }
      const remaining = total - completed;
      if (criticalMissing.length > 0) {
        return `${remaining} items incomplete, including critical: ${criticalMissing[0]?.replace(/([A-Z])/g, ' $1').trim()}.`;
      }
      return `${completed}/${total} planning items complete. ${remaining} more to go for full protection.`;
    }

    case 'lifetimeIncomeSecurity': {
      const coverage = (details.coreCoveragePct as number) ?? 0;
      const monthlyIncome = (details.guaranteedLifetimeIncomeMonthly as number) ?? 0;

      if (details.needsDataEntry) {
        return "Enter your income sources and expenses to see retirement income coverage.";
      }
      if (status === 'GREEN') {
        return `${formatCurrency(monthlyIncome)}/month guaranteed covers ${formatPct(coverage, 0)} of essentials. Market-proof income.`;
      }
      return `Only ${formatPct(coverage, 0)} of essentials covered by guaranteed income. ${formatPct(1 - coverage, 0)} depends on markets.`;
    }

    case 'performanceOptimization':
    case 'performanceMetrics': {
      const pctOfTarget = (details.pctOfTarget as number) ?? 100;
      const improvement = (details.relativeImprovement as number) ?? 0;

      if (status === 'GREEN') {
        return `Performing at ${pctOfTarget.toFixed(0)}% of risk-adjusted target. Returns match the risk you're taking.`;
      }
      return `${pctOfTarget.toFixed(0)}% of target performance. Optimization could improve returns by ~${formatPct(improvement, 0)}.`;
    }

    case 'crossAccountConcentration': {
      const topHoldings = details.topHoldings as Array<{ ticker: string; weight: number; accountCount: number }> | undefined;
      const multiAccount = details.multiAccountTickers as Array<{ ticker: string; weight: number }> | undefined;
      const top = topHoldings?.[0];

      if (!top) return "Link multiple accounts to analyze cross-account concentration.";

      if (multiAccount && multiAccount.length > 0) {
        const overlap = multiAccount[0];
        return `${overlap.ticker} appears in multiple accounts, totaling ${formatPct(overlap.weight)}. Hidden concentration risk.`;
      }
      if (status === 'GREEN') {
        return `Largest combined position (${top.ticker}) is ${formatPct(top.weight)}. Accounts are well-balanced.`;
      }
      return `${top.ticker} is ${formatPct(top.weight)} across all accounts—significant single-security exposure.`;
    }

    default:
      if (status === 'GREEN') return "This metric is within healthy parameters.";
      if (status === 'YELLOW') return "This metric warrants attention but isn't critical.";
      return "This metric needs review—tap for details.";
  }
};

// Get action button text based on status (neutral language per compliance requirements)
const getActionText = (status: 'GREEN' | 'YELLOW' | 'RED'): string => {
  switch (status) {
    case 'RED':
      return 'View Details';
    case 'YELLOW':
      return 'Learn More';
    case 'GREEN':
      return 'View Details';
    default:
      return 'View Details';
  }
};

export function DiagnosticCard({ 
  name, 
  subtitle,
  categoryKey, 
  result, 
  onViewDetails,
  scoringConfig = DEFAULT_SCORING_CONFIG,
  riskTolerance = 'Moderate',
  animationDelay = 0,
}: DiagnosticCardProps) {

  const getStatusGradient = () => {
    if (result.status === 'GREEN') return 'card-status-good';
    if (result.status === 'YELLOW') return 'card-status-warning';
    return 'card-status-critical';
  };

  const getGlowClass = () => {
    if (result.status === 'GREEN') return 'hover:glow-good';
    if (result.status === 'YELLOW') return 'hover:glow-warning';
    return 'hover:glow-critical';
  };

  const getStatusBorderClass = () => {
    if (result.status === 'GREEN') return 'border-l-status-good';
    if (result.status === 'YELLOW') return 'border-l-status-warning';
    return 'border-l-status-critical';
  };

  const getStatusIcon = () => {
    if (result.status === 'GREEN') return <CheckCircle2 className="h-5 w-5 text-status-good" />;
    if (result.status === 'YELLOW') return <AlertTriangle className="h-5 w-5 text-status-warning" />;
    return <XCircle className="h-5 w-5 text-status-critical" />;
  };

  const getMetricColor = () => {
    if (result.status === 'GREEN') return 'text-status-good';
    if (result.status === 'YELLOW') return 'text-status-warning';
    return 'text-status-critical';
  };

  const plainEnglish = getDataDrivenExplanation(result.status, categoryKey, result.details);
  const actionText = getActionText(result.status);

  return (
    <Card
      className={cn(
        'card-interactive cursor-pointer group border-l-4 touch-manipulation overflow-hidden',
        'transition-all duration-300 rounded-2xl',
        'flex flex-col h-full min-h-[380px]',
        getStatusGradient(),
        getGlowClass(),
        getStatusBorderClass(),
        animationDelay > 0 && 'opacity-0',
      )}
      style={{
        animation: animationDelay > 0 ? `cardEntrance 0.5s ease-out ${animationDelay * 0.1}s forwards` : undefined,
      }}
      onClick={onViewDetails}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onViewDetails();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${name} diagnostic: ${result.status === 'GREEN' ? 'No issues detected' : result.status === 'YELLOW' ? 'Observation noted' : 'Review recommended'}. ${result.keyFinding}`}
    >
      <CardHeader className="pb-2 p-4 sm:p-5 sm:pb-3">
        <div className="flex flex-col gap-2">
          {/* Row 1: Status badge */}
          <div className="flex justify-end">
            <StatusBadge status={result.status} label={STATUS_LABELS[result.status]} showLabel size="sm" />
          </div>
          {/* Row 2: Icon + Title + Education */}
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <CardTitle className="text-base sm:text-lg font-semibold">{name}</CardTitle>
            <EducationPopup 
              categoryKey={categoryKey} 
              scoringConfig={scoringConfig}
              riskTolerance={riskTolerance}
            />
          </div>
          {/* Row 3: Subtitle - flows naturally as a sentence */}
          {subtitle && (
            <p className="text-xs text-muted-foreground leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-4 sm:p-5 pt-0 sm:pt-0 flex flex-col flex-grow">
        <div className="space-y-4 flex-grow">
        {/* Hero Metric Display - responsive font based on content length */}
        {result.headlineMetric && (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={cn(
              'font-mono font-bold tracking-tight break-words',
              // Use smaller font for long metrics (like "Top Position: 13.2% | Top 10: 75%")
              result.headlineMetric.length > 15
                ? 'text-lg sm:text-xl'
                : 'text-2xl sm:text-3xl',
              getMetricColor()
            )}>
              {result.headlineMetric}
            </span>
            {result.score !== undefined && result.headlineMetric.length <= 15 && (
              <span className="text-sm text-muted-foreground">
                score
              </span>
            )}
          </div>
        )}

        {/* Mini Visualizations - conditionally rendered based on category */}
        {(categoryKey === 'riskDiversification' || categoryKey === 'diversification') && (
          (() => {
            const topPositions = result.details.topPositions as Array<{ ticker: string; weight: number }> | undefined;
            const sectorWeights = result.details.sectorWeights as Record<string, number> | undefined;

            // Convert sector weights to array format
            const sectorData = sectorWeights
              ? Object.entries(sectorWeights)
                  .map(([name, weight]) => ({ name, weight }))
                  .sort((a, b) => b.weight - a.weight)
              : [];

            if (topPositions && topPositions.length > 0) {
              const chartData = topPositions.slice(0, 5).map(p => ({
                name: p.ticker,
                value: p.weight,
              }));
              const othersWeight = topPositions.slice(5).reduce((sum, p) => sum + p.weight, 0);
              if (othersWeight > 0) {
                chartData.push({ name: 'Other', value: othersWeight });
              }
              return (
                <div className="space-y-3 py-2">
                  {/* Position breakdown */}
                  <div className="flex items-center gap-3">
                    <MiniDonutChart data={chartData} size={60} innerRadius={15} />
                    <div className="flex-1 space-y-0.5">
                      {topPositions.slice(0, 3).map((p) => (
                        <div key={p.ticker} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground truncate">{p.ticker}</span>
                          <span className="font-mono">{(p.weight * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Sector breakdown if available */}
                  {sectorData.length > 0 && (
                    <div className="pt-2 border-t border-border/50">
                      <p className="text-[10px] text-muted-foreground mb-1.5">Sector Exposure</p>
                      <SectorBreakdown sectors={sectorData} />
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })()
        )}

        {categoryKey === 'downsideResilience' && (
          (() => {
            const portfolioValue = (result.details.portfolioValue as number) ?? 500000;
            // Get real scenario data from analysis engine
            const scenarios = (result.details.scenarios as Array<{ name: string; portfolioImpact: number; spImpact: number }>) ?? [];

            if (portfolioValue > 0) {
              return (
                <div className="py-2">
                  <StressTestChart
                    currentValue={portfolioValue}
                    scenarios={scenarios}
                    protectionFactor={0.6} // 40% loss reduction with tactical management
                  />
                </div>
              );
            }
            return null;
          })()
        )}

        {categoryKey === 'costAnalysis' && (
          (() => {
            const allInFees = (result.details.allInFees as number) ?? 0;
            const benchmarkFees = 0.003; // 0.30% typical index fund benchmark
            const portfolioValue = (result.details.portfolioValue as number) ?? 100000;
            if (allInFees > 0) {
              return (
                <div className="space-y-3 my-2">
                  {/* Fee comparison bar */}
                  <HorizontalCompareBar
                    yourValue={allInFees}
                    benchmarkValue={benchmarkFees}
                    label="Annual Fees"
                    yourLabel="Your fees"
                    benchmarkLabel="Index avg"
                    format="percent"
                  />
                  {/* Potential savings if fees are higher than benchmark */}
                  {allInFees > benchmarkFees && (
                    <PotentialSavings
                      currentFees={allInFees}
                      optimizedFees={benchmarkFees}
                      years={10}
                      portfolioValue={portfolioValue}
                    />
                  )}
                </div>
              );
            }
            return null;
          })()
        )}

        {categoryKey === 'taxEfficiency' && (
          (() => {
            const harvestable = (result.details.totalHarvestable as number) ?? 0;
            const taxSavings = (result.details.estimatedTaxSavings as number) ?? 0;
            if (harvestable > 0 && taxSavings > 0) {
              return (
                <div className="p-3 rounded-lg bg-status-good/10 border border-status-good/20 my-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Tax-Loss Harvest Opportunity</span>
                    <TrendingDown className="h-4 w-4 text-status-good" />
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-xl font-bold text-status-good">
                      ${taxSavings >= 1000 ? `${(taxSavings / 1000).toFixed(1)}K` : taxSavings.toFixed(0)}
                    </span>
                    <span className="text-xs text-muted-foreground">potential savings</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    From ${harvestable.toLocaleString()} in harvestable losses
                  </p>
                </div>
              );
            }
            return null;
          })()
        )}

        {(categoryKey === 'performanceOptimization' || categoryKey === 'performanceMetrics') && (
          (() => {
            const sharpeRatio = (result.details.sharpeRatio as number);
            const targetSharpe = (result.details.targetSharpe as number) ?? 0.5;
            const optimizedSharpe = (result.details.optimizedSharpe as number);

            // Only render if we have real sharpe data
            if (sharpeRatio !== undefined) {
              return (
                <div className="py-2">
                  <SharpeComparison
                    yourSharpe={sharpeRatio}
                    benchmarkSharpe={targetSharpe}
                    potentialSharpe={optimizedSharpe}
                  />
                </div>
              );
            }
            return null;
          })()
        )}

        {/* Key Finding */}
        <p className="text-sm text-foreground/90 line-clamp-4 leading-relaxed">
          {result.keyFinding}
        </p>
        </div>

        {/* Sticky Bottom Section: Plain English + Action Button */}
        <div className="pt-4 mt-auto space-y-3">
          {/* Plain English Explanation */}
          <div className="p-3 rounded-xl bg-muted/30 border border-border/50">
            <p className="text-xs text-muted-foreground flex items-start gap-2">
              <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
              <span className="italic">{plainEnglish}</span>
            </p>
          </div>

          {/* Action Button */}
          <Button
            variant={result.status === 'RED' ? 'destructive' : result.status === 'YELLOW' ? 'secondary' : 'outline'}
            size="sm"
            className={cn(
              'w-full group-hover:translate-x-0 transition-all',
              result.status === 'GREEN' && 'hover:bg-status-good/10 hover:text-status-good hover:border-status-good/30'
            )}
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
          >
            {actionText}
            <ArrowRight size={14} className="ml-1.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Compact variant for mobile carousels
export function DiagnosticCardCompact({
  name,
  categoryKey: _categoryKey,
  result,
  onViewDetails,
}: Pick<DiagnosticCardProps, 'name' | 'categoryKey' | 'result' | 'onViewDetails'>) {

  const getStatusGradient = () => {
    if (result.status === 'GREEN') return 'card-status-good';
    if (result.status === 'YELLOW') return 'card-status-warning';
    return 'card-status-critical';
  };

  const getStatusBorderClass = () => {
    if (result.status === 'GREEN') return 'border-l-status-good';
    if (result.status === 'YELLOW') return 'border-l-status-warning';
    return 'border-l-status-critical';
  };

  const getMetricColor = () => {
    if (result.status === 'GREEN') return 'text-status-good';
    if (result.status === 'YELLOW') return 'text-status-warning';
    return 'text-status-critical';
  };

  return (
    <Card
      className={cn(
        'card-interactive cursor-pointer border-l-4 touch-manipulation min-w-[280px] snap-center',
        'transition-all duration-300 rounded-2xl',
        getStatusGradient(),
        getStatusBorderClass(),
      )}
      onClick={onViewDetails}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onViewDetails();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${name}: ${result.status === 'GREEN' ? 'No issues detected' : result.status === 'YELLOW' ? 'Observation noted' : 'Review recommended'}`}
    >
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm truncate pr-2">{name}</h3>
          <StatusBadge status={result.status} size="sm" />
        </div>
        
        {result.headlineMetric && (
          <div className={cn('text-2xl font-mono font-bold', getMetricColor())}>
            {result.headlineMetric}
          </div>
        )}
        
        <p className="text-xs text-muted-foreground line-clamp-2">
          {result.keyFinding}
        </p>
        
        <button className="flex items-center text-xs font-medium text-primary">
          View details
          <ChevronRight size={12} className="ml-0.5" />
        </button>
      </CardContent>
    </Card>
  );
}
